import {
  avatarButtonContainerStyles,
  avatarContainerStyles,
  seeImageIconStyles
} from '../organization-basic-information-styles'
import { Alert, Box, IconButton, Skeleton, Snackbar, Tooltip, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HideImageIcon from '@mui/icons-material/HideImage'
import { FormDialog, AlertDialog } from 'components'
import DeleteIcon from '@mui/icons-material/Delete'
import React, { useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import { createOrgUrl, deleteOrgUrl, type OrgUrl, updateOrgUrl } from 'services'
import { useSessionContext } from 'session'

interface OrganizationLogoProps {
  loading: boolean
  originalLogoUrl: OrgUrl | undefined
  orgId: number
}
export const OrganizationLogo: React.FC <OrganizationLogoProps> = ({ loading, originalLogoUrl, orgId }) => {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = useState(false)
  const [imageURL, setImageURL] = useState<OrgUrl | undefined>(originalLogoUrl)
  const [imageURLNewValue, setImageURLNewValue] = useState<string>('')
  const [deleteLogoConfirmationOpen, setDeleteLogoConfirmationOpen] = useState<boolean>(false)
  const { openNewTab } = useSessionContext()

  const iconButtonProps = {
    color: 'primary',
    disabled: loading,
    children: <EditIcon/>
  }

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }

    setShowSuccessAlert(false)
    setShowUnsuccessAlert(false)
  }

  const createNewLogo = async (newUrl: OrgUrl): Promise<void> => {
    await createOrgUrl(newUrl)
      .then((urlId) => {
        newUrl.id = urlId
        setImageURL(newUrl)
        setShowSuccessAlert(true)
      })
      .catch(() => {
        setShowUnsuccessAlert(true)
      })
  }

  const updateLogo = async (newUrlValue: string): Promise<void> => {
    if (imageURL != null) {
      const updatedUrl: OrgUrl = { ...imageURL, value: newUrlValue }
      updateOrgUrl(updatedUrl)
        .then(() => {
          const updatedState = { ...imageURL, value: newUrlValue }
          setImageURL(updatedState)
          setShowSuccessAlert(true)
        })
        .catch(() => {
          setShowUnsuccessAlert(true)
        })
    }
  }

  const handleNewLogoImage = (): void => {
    if (imageURL != null) {
      void updateLogo(imageURLNewValue)
    } else {
      const newUrl: OrgUrl = {
        descId: 5,
        displayOrder: 1,
        id: -1,
        typeId: 2,
        value: imageURLNewValue,
        orgId,
        lastUpdated: ''
      }

      void createNewLogo(newUrl)
    }
  }

  const handleDeleteLogoClick = (): void => {
    setDeleteLogoConfirmationOpen(true)
  }

  const handleDeleteLogoConfirmationClose = (): void => {
    setDeleteLogoConfirmationOpen(false)
  }

  const handleDeleteLogoConfirmed = (): void => {
    if (imageURL != null) {
      void deleteOrgUrl(imageURL).then(value => {
        setImageURL(undefined)
        setDeleteLogoConfirmationOpen(false)
        if (value) { setShowSuccessAlert(true) } else { setShowUnsuccessAlert(true) }
      })
    }
  }
  return (
        <Box sx={avatarContainerStyles}>
            {showSuccessAlert && (
                <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                        This organization Logo has been successfully updated!
                    </Alert>
                </Snackbar>
            )}
            {showUnsuccessAlert && (
                <Snackbar open={showUnsuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                        Failed to update Organization Logo
                    </Alert>
                </Snackbar>
            )}
          <AlertDialog
              title="Delete Confirmation"
              contentText={'Are you sure you want to delete this logo?'}
              nameButtonCancel="Cancel"
              nameButtonConfirm="Delete"
              onButtonClick={handleDeleteLogoConfirmed}
              onClose={handleDeleteLogoConfirmationClose}
              open={deleteLogoConfirmationOpen}
          />
            <Typography variant="subtitle2">
                Image/Logo
            </Typography>
            {!loading
              ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={avatarButtonContainerStyles}>
                            <Tooltip title={(imageURL != null) ? 'Show Logo' : 'No Logo to show'}>
                                <Box sx={avatarButtonContainerStyles}>
                                    <IconButton sx={{ width: '70%' }} disabled={imageURL == null} onClick={() => {
                                      if (imageURL != null) openNewTab(imageURL.value)
                                    }}>
                                        {(imageURL != null)
                                          ? <VisibilityIcon sx={seeImageIconStyles} color='primary'/>
                                          : <HideImageIcon sx={seeImageIconStyles} color='disabled'/>
                                        }
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </Box>
                        <Box sx={avatarButtonContainerStyles}>
                            <FormDialog isShowCheckBox={false}
                                        onCheckboxChange={(isChecked, queryName) => {
                                          setImageURLNewValue(queryName)
                                        }}
                                        onButtonClick={() => {
                                          handleNewLogoImage()
                                        }} title='New value for logo' contentText='URL for new Logo'
                                        nameLabelField='Logo Url' iconButtonProps={iconButtonProps}
                                        tooltip='Edit Logo'/>
                            <Tooltip title="Delete Logo">
                                <Box>
                                    <IconButton disabled={loading || (imageURL == null)} aria-label="Delete Logo"
                                                color="secondary" onClick={() => {
                                                  handleDeleteLogoClick()
                                                }}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>

                )
              : (
                    <Skeleton variant="rectangular" width={150} height={220}/>
                )}
        </Box>
  )
}
